import { LocaleCode } from '../../types/lang.types';

function getIsoDate(date: Date): string {
    return date.toISOString().substring(0, 19);
}

function getDateString(date: string): string {
    return date.substring(0, 10);
}

function getYearAsNumber(date: string): number {
    return Number(date.substring(0, 4));
}

function getMonthAsNumber(date: string): number {
    return Number(date.substring(5, 7));
}

function getDayAsNumber(date: string): number {
    return Number(date.substring(8, 10));
}

function addLeadingZero(number: number): string {
    if (number < 10) {
        return '0' + number;
    } else {
        return number.toString();
    }
}

function dayIsGivenDay(day: number, month: number, year: number, date: string): boolean {
    return (
        DateUtility.getYearAsNumber(date) == year &&
        DateUtility.getMonthAsNumber(date) == month &&
        DateUtility.getDayAsNumber(date) == day
    );
}

/**
 * Checks if a given day falls within a date range.
 * @param day Day of the month (1-31)
 * @param month Month (1-12)
 * @param year Year (e.g., 2025)
 * @param start Start date string in YYYY-MM-DD format
 * @param end End date string in YYYY-MM-DD format
 * @returns True if the day falls within the range, false otherwise
 */
function dayIsInRange(day: number, month: number, year: number, start: string, end: string): boolean {
    if (!start && end) {
        return false;
    } else {
        const startDate = new Date(
            DateUtility.getYearAsNumber(start),
            DateUtility.getMonthAsNumber(start) - 1,
            DateUtility.getDayAsNumber(start)
        );
        const endDate = new Date(
            DateUtility.getYearAsNumber(end),
            DateUtility.getMonthAsNumber(end) - 1,
            DateUtility.getDayAsNumber(end)
        );
        const given = new Date(year, month - 1, day);
        return given >= startDate && given <= endDate;
    }
}

function dayIsBeforeMinimalDate(day: number, month: number, year: number, offset: number): boolean {
    const minimal = new Date();
    minimal.setDate(minimal.getDate() + offset);

    const given = new Date(year, month - 1, day);

    minimal.setHours(0, 0, 0, 0);
    given.setHours(0, 0, 0, 0);

    return given < minimal;
}
function dayIsClosedDay(
    day: number,
    month: number,
    year: number,
    openingHours: number[],
    specialSelectableDates?: string[]
): boolean {
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay();

    // Check if this specific date is in the special selectable dates list
    // specialSelectableDates should already be normalized to YYYY-MM-DD format
    if (specialSelectableDates && specialSelectableDates.length > 0) {
        const dateString = `${year}-${addLeadingZero(month)}-${addLeadingZero(day)}`;
        // Compare directly since dates are already normalized
        if (specialSelectableDates.includes(dateString)) {
            return false; // Date is selectable because it's in special dates
        }
    }

    return !openingHours.includes(dayOfWeek);
}

function getMillisecondsUntilMidnight(date: Date = new Date()): number {
    const tomorrow = new Date(date);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow.getTime() - date.getTime();
}

function isValidDateString(date: string): boolean {
    return Boolean(date && /^\d{4}-\d{2}-\d{2}$/.test(date) && !isNaN(Date.parse(date)));
}

/**
 * Formats a date for screen reader accessibility.
 * Returns a localized full date string with weekday, or a fallback format if localization fails.
 * @param day Day of the month (1-31)
 * @param month Month (1-12)
 * @param year Year (e.g., 2025)
 * @param lang Language code (e.g., 'de-CH', 'fr-CH')
 * @returns Formatted date string for screen readers
 */
function formatDateForScreenReader(day: number, month: number, year: number, lang: string): string {
    try {
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString(lang, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch {
        return `${day}. ${month}. ${year}`;
    }
}

function safeCallDateUtility<T>(fn: () => T, fallback: T, context: string): T {
    try {
        return fn();
    } catch (error) {
        console.error(`Calendar DateUtility error in ${context}:`, error);
        return fallback;
    }
}

/**
 * Gets the localized name of a weekday.
 * Uses Intl.DateTimeFormat for localization, with fallback to German weekday names.
 * @param dayOfWeek Day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
 * @param lang Language code (e.g., 'de-CH', 'fr-CH')
 * @param format Format type: 'long' for full name, 'short' for abbreviated
 * @returns Localized weekday name with first letter capitalized
 */
function getLocalizedWeekdayName(dayOfWeek: number, lang: string, format: 'long' | 'short' = 'short'): string {
    try {
        const formatter = new Intl.DateTimeFormat(lang, { weekday: format });
        const baseSunday = new Date(2023, 0, 1);
        const targetDate = new Date(baseSunday);
        targetDate.setDate(baseSunday.getDate() + dayOfWeek);

        const weekdayName = formatter.format(targetDate);
        return weekdayName.charAt(0).toUpperCase() + weekdayName.slice(1);
    } catch (error) {
        console.warn('DateUtility: Error getting localized weekday name:', error);
        const weekdayNames =
            format === 'long'
                ? ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag']
                : ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
        return weekdayNames[dayOfWeek] || `Day ${dayOfWeek}`;
    }
}

function getFirstDayOfWeek(lang: string): number {
    try {
        const firstDayMapping: { [key: string]: number } = {
            [LocaleCode.DE_CH]: 1,
            [LocaleCode.FR_CH]: 1,
        };

        return firstDayMapping[lang] ?? 1;
    } catch {
        return 1;
    }
}

function getWeekdayOrder(lang: string): number[] {
    const firstDay = getFirstDayOfWeek(lang);
    return Array.from({ length: 7 }, (_, i) => (firstDay + i) % 7);
}

/**
 * Converts a time string (HH:MM) to minutes since midnight.
 * @param timeString Time in HH:MM format (e.g., "10:00")
 * @returns Minutes since midnight, or -1 if invalid
 */
function timeToMinutes(timeString: string): number {
    if (!timeString) {
        return -1;
    }
    const parts = timeString.split(':');
    if (parts.length !== 2 || !parts[0] || !parts[1]) {
        return -1;
    }
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(minutes)) {
        return -1;
    }
    return hours * 60 + minutes;
}

/**
 * Converts minutes since midnight to a time string (HH:MM).
 * @param minutes Minutes since midnight
 * @returns Time string in HH:MM format
 */
function minutesToTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${addLeadingZero(hours)}:${addLeadingZero(mins)}`;
}

export const DateUtility = {
    getIsoDate,
    getDateString,
    getYearAsNumber,
    getMonthAsNumber,
    getDayAsNumber,
    addLeadingZero,
    dayIsClosedDay,
    dayIsGivenDay,
    dayIsInRange,
    dayIsBeforeMinimalDate,
    getMillisecondsUntilMidnight,
    isValidDateString,
    formatDateForScreenReader,
    safeCallDateUtility,
    getLocalizedWeekdayName,
    getFirstDayOfWeek,
    getWeekdayOrder,
    timeToMinutes,
    minutesToTime,
};
